"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Box, Weight, MapPin, Truck, Layers, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const shippingFactorsData = [{
  code: "01",
  title: "Machine & Package Size",
  description: "Larger packages can require different shipping arrangements.",
  icon: Box,
  spanClass: "lg:col-span-2"
}, {
  code: "02",
  title: "Total Shipment Weight",
  description: "Use the actual packed weight where available.",
  icon: Weight,
  spanClass: "lg:col-span-2"
}, {
  code: "03",
  title: "Destination",
  description: "Distance and location can affect the delivery arrangement.",
  icon: MapPin,
  spanClass: "lg:col-span-2"
}, {
  code: "04",
  title: "Unloading Requirements",
  description: "Receiving conditions may affect the service required.",
  icon: Truck,
  spanClass: "lg:col-span-3"
}, {
  code: "05",
  title: "Separate Components",
  description: "The bull, inflatable or accessories may be packed separately depending on the order.",
  icon: Layers,
  spanClass: "lg:col-span-3"
}];
export default function ShippingFactors() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: (customIndex: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: shouldReduceMotion ? 0 : customIndex * 0.05,
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };
  return <section id="bullshippingcostfactors" data-nav="light" className="relative w-full bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-7xl mx-auto">
        {/* Header Block */}
        <div className="text-center max-w-4xl mx-auto mb-14 md:mb-20">
          <div className="inline-block font-mono text-xs uppercase tracking-widest text-muted-foreground border border-border px-3 py-1 mb-4 bg-muted/40">
            Logistics & Crating Criteria
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            What Affects Mechanical Bull Shipping Cost?
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            There is no useful single shipping price for every mechanical bull. Delivery cost can vary according to the equipment being shipped, packed dimensions, weight, origin, destination, access requirements and the delivery service available for that order.
          </p>
        </div>

        {/* 5-Card Factor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
          {shippingFactorsData.map((factor, factorIndex) => {
          const IconComponent = factor.icon;
          return <motion.div key={factor.code} data-index={factorIndex} custom={factorIndex} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} className={`bg-card text-card-foreground border border-border rounded-none p-6 sm:p-8 flex flex-col justify-between transition-[border-color] duration-200 hover:border-foreground ${factor.spanClass}`}>
                <div>
                  <div className="flex items-center justify-between pb-6 border-b border-border/60 mb-6">
                    <span className="font-mono text-xs tracking-wider text-muted-foreground">
                      FACTOR {factor.code}
                    </span>
                    <div className="p-2 border border-border bg-muted/30 text-foreground">
                      <IconComponent className="size-4" aria-hidden="true" />
                    </div>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-card-foreground mb-3">
                    {factor.title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {factor.description}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-border/40 flex items-center justify-between font-mono text-[11px] text-muted-foreground/70 uppercase">
                  <span>Standard Freight</span>
                  <span>Specification Factor</span>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Closing Advisory & Action */}
        <div className="mt-12 md:mt-16 border border-border bg-muted/20 p-8 sm:p-10 text-center max-w-4xl mx-auto">
          <p className="font-sans text-base sm:text-lg text-foreground font-medium mb-6 max-w-2xl mx-auto">
            Contact us with the product name or SKU and delivery destination when you need shipping information for a specific machine.
          </p>
          <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 h-auto font-serif uppercase tracking-wider text-base font-semibold shadow-none transition-colors">
            <Link to="/ContactPage">
              Ask About Shipping
              <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}